import React from 'react';
import { ArrowLeftRightIcon, ClockIcon, LockIcon, PlayIcon, ShieldAlertIcon, XIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { frequencyLabel, groupLabels } from '../../data/labels';
import { equalShares, isDoneStatus } from '../../utils/progress';
import { namesOf } from '../../utils/members';
import { isGivenAway } from '../../utils/today';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { StatusBadge } from '../StatusBadge';
import { SyncTag } from '../SyncTag';
export function TaskDetailSheet() {
  const {
    taskId,
    openTask,
    requestTimerStart,
    openManualEntry,
    openOffer,
    openException
  } = useUi();
  const {
    templateById,
    getOcc,
    family,
    viewer,
    isAdmin,
    minutesToday,
    checkTrust,
    uncheck,
    submitForReview,
    memberById,
    cancelOffer,
    takeOffer,
    declineOffer
  } = useAppState();
  const template = templateById(taskId ?? undefined);
  const close = () => openTask(null);
  if (!template) return <Sheet open={false} onClose={close} title="" children={null} data-id="element-1012" data-mp="2x9pjna75kcsq" />;
  const occ = getOcc(template.id);
  const done = isDoneStatus(occ.status);
  const trust = template.confirmation === 'zaupanje';
  const minutes = template.targetMinutes ? minutesToday(template.id) : 0;
  const shared = template.assigneeIds.length === 0;
  const mine = template.assigneeIds.includes(viewer.id);
  const given = isGivenAway(occ, viewer.id);
  const canAct = mine && !given || occ.takenBy === viewer.id || shared;
  const offeredToMe = occ.offer && !occ.takenBy && occ.offer.byId !== viewer.id && (occ.offer.toId === 'vsi' || occ.offer.toId === viewer.id);
  const shares = template.baseValue ? equalShares(template.baseValue, template.assigneeIds) : undefined;
  const pts = template.valueType === 'bonus' ? 'goodies' : 'cvenkotočk';
  const value = !template.baseValue ? 'Brez točk' : shared ? `${template.baseValue} goodies za tistega, ki izvede (skupna skrb)` : template.assigneeIds.length > 1 ? `${template.baseValue} ${pts} skupaj, deljeno: ${shares?.[viewer.id] ?? 0} na izvajalca (starš lahko spremeni deleže)` : `${template.baseValue} ${pts}`;
  const facts: [string, string][] = [['Nosilci', shared ? 'Sprotni dogovor – kdor utegne' : namesOf(family, template.assigneeIds)], ['Potrjevanje', trust ? 'Na zaupanje – obkljukaš sam' : 'Celotno opravilo potrdi skrbnik'], ['Vrednost', value], ['Ponavljanje', frequencyLabel(template.frequency)]];
  if (template.targetMinutes) facts.push(['Cilj', `${template.targetMinutes} min · danes ${minutes} min`]);
  if (template.dueLabel) facts.push(['Rok', template.dueLabel]);
  if (template.description) facts.push(['Opis', template.description]);
  facts.push(['Prenos', template.transferable ? 'Prenosljivo – lahko ga ponudiš drugemu članu za ta termin' : 'Osebna obveznost – drug član je ne more opraviti namesto tebe']);
  if (occ.performerId) facts.push(['Izvedel(a)', memberById(occ.performerId)?.name ?? '']);
  let primary: React.ReactNode = null;
  if (offeredToMe) {
    primary = <div className="grid grid-cols-2 gap-2" data-id="element-1013" data-mp="g269ofs8im4r">
        {occ.offer?.toId === viewer.id ? <Button variant="secondary" onClick={() => {
        declineOffer(template.id);
        close();
      }} data-id="element-1014" data-mp="22ohdqfqcqqn">Zavrni</Button> : <span data-id="element-1015" data-mp="721er5489g7h" />}
        <Button onClick={() => takeOffer(template.id)} data-id="element-1016" data-mp="uq101pirseeo">Prevzamem</Button>
      </div>;
  } else if (given) {
    primary = <p className="text-center text-sm font-semibold text-muted" data-id="element-1017" data-mp="yqb7z7q4hew6">Ta termin opravi {memberById(occ.takenBy)?.name}. Točke dobi dejanski izvajalec.</p>;
  } else if (!canAct) {
    primary = <p className="text-center text-sm font-semibold text-muted" data-id="element-1018" data-mp="pyj094shgjva">To ni tvoja naloga – samo pregled.</p>;
  } else if (trust) {
    primary = done ? occ.performerId && occ.performerId !== viewer.id ? <p className="text-center text-sm font-semibold text-done-ink" data-id="element-1019" data-mp="1ifllqg6b5prn">Opravil(a) {memberById(occ.performerId)?.name}. Hvala!</p> : <Button variant="secondary" className="w-full" onClick={() => uncheck(template.id)} data-id="element-1020" data-mp="3q08143v7s2xl">
          Ups, označi kot neopravljeno
        </Button> : <Button variant="success" className="w-full" onClick={() => checkTrust(template.id)} data-id="element-1021" data-mp="3gz9vjh54nncw">
        {shared ? 'Jaz sem naredil(a)' : 'Opravljeno'}
      </Button>;
  } else if (occ.status === 'odprto' || occ.status === 'popravek') {
    primary = <Button className="w-full" onClick={() => submitForReview(template.id)} data-id="element-1022" data-mp="8if4r01lttf6">
        {occ.status === 'popravek' ? 'Ponovno oddaj v pregled' : 'Oddaj v pregled'}
      </Button>;
  } else if (occ.status === 'v_pregledu') {
    primary = <p className="text-center text-sm font-semibold text-review-ink" data-id="element-1023" data-mp="3uyt245iaiqn3">Čaka na potrditev skrbnika. Drži pesti.</p>;
  } else if (occ.status === 'potrjeno') {
    primary = <p className="flex items-center justify-center gap-2 text-sm font-semibold text-done-ink" data-id="element-1024" data-mp="rhkqi3e5on0t">
        <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1025" data-mp="ysparanrdbkm" /> Potrjeno in zaklenjeno – točke se ne štejejo dvakrat
      </p>;
  }
  const canOffer = template.transferable && mine && !given && !done && occ.status !== 'v_pregledu' && !occ.offer;
  const ownOffer = occ.offer && occ.offer.byId === viewer.id && !occ.takenBy;
  return <Sheet open onClose={close} title={template.title} subtitle={`${groupLabels[template.group]} · Družina ${family.name}`} footer={primary} data-id="element-1026" data-mp="1ct9s02e0yzc0">
      <div className="flex flex-wrap items-center gap-2" data-id="element-1027" data-mp="1hph2m8hd8z0a">
        <StatusBadge status={occ.status} data-id="element-1028" data-mp="1mp3gmk50qja5" />
        {occ.status === 'odprto' && <span className="text-sm font-semibold text-muted" data-id="element-1029" data-mp="1rgl6ac1lcxft">Odprto</span>}
        {(occ.status !== 'odprto' || occ.sync === 'pending') && <SyncTag sync={occ.sync} data-id="element-1030" data-mp="1wewg4mrvz7y1" />}
        {ownOffer && <span className="rounded-full bg-info-soft px-2 py-0.5 text-xs font-bold text-info-ink" data-id="element-1031" data-mp="20i2vql0w2bh6">
            Ponujeno {occ.offer?.toId === 'vsi' ? 'vsem' : memberById(occ.offer?.toId)?.name} – do sprejema tvoja odgovornost
          </span>}
      </div>

      {occ.status === 'popravek' && occ.correctionNote && <div className="mt-3 rounded-2xl bg-accent-soft p-3 text-sm" data-id="element-1032" data-mp="1e2qeozb17wf">
          <p className="font-bold text-accent-ink" data-id="element-1033" data-mp="f46770ff09eh">Popravek – {memberById(occ.reviewedBy)?.name ?? 'skrbnik'}:</p>
          <p className="mt-0.5" data-id="element-1034" data-mp="ad7qw0uffbb8">{occ.correctionNote}</p>
        </div>}

      {occ.status === 'potrjeno' && occ.shares && <div className="mt-3 rounded-2xl bg-done-soft p-3 text-sm" data-id="element-1035" data-mp="p12h9akyp3l5">
          <p className="font-bold text-done-ink" data-id="element-1036" data-mp="k4c2cqkw8e1b">Potrdil(a) {memberById(occ.reviewedBy)?.name ?? 'skrbnik'}</p>
          <p className="mt-0.5" data-id="element-1037" data-mp="y290hrezwsi3">
            {Object.entries(occ.shares).map(([id, p]) => `${memberById(id)?.name}: ${p}`).join(' · ')}{' '}
            točk
          </p>
        </div>}

      <dl className="mt-4 divide-y divide-line rounded-2xl border-[1.5px] border-line" data-id="element-1038" data-mp="2zv6x7ybw858b">
        {facts.map(([k, v]) => <div key={k} className="grid grid-cols-[104px_1fr] gap-3 px-3 py-2.5 text-sm" data-id="element-1039" data-mp="2uxyf1xqqw172">
            <dt className="font-semibold text-muted" data-id="element-1040" data-mp="1afmie8cf17ac">{k}</dt>
            <dd data-id="element-1041" data-mp="1fbl2vkumi55h">{v}</dd>
          </div>)}
      </dl>

      {canAct && !done && occ.status !== 'v_pregledu' && <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-1042" data-mp="11kpyolv2kc88">
          {template.timed && <Button variant="secondary" onClick={() => requestTimerStart({
        templateId: template.id
      })} data-id="element-1043" data-mp="15li9vkni4r75">
              <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-1044" data-mp="rlgyhb9m21df" /> Začni časovnik
            </Button>}
          {template.timed && <Button variant="secondary" onClick={() => openManualEntry(template.id)} data-id="element-1045" data-mp="imdgkam6js0i">
              <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1046" data-mp="ioq7hd9x1cpp" /> Vpiši čas
            </Button>}
          {canOffer && <Button variant="secondary" onClick={() => openOffer(template.id)} data-id="element-1047" data-mp="2czv2jt6xsgtf">
              <ArrowLeftRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1048" data-mp="2h1q5f24in29b" /> Ponudi
            </Button>}
          {ownOffer && <Button variant="secondary" onClick={() => cancelOffer(template.id)} data-id="element-1049" data-mp="14rbjhaokub6e">
              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-1050" data-mp="zta16ryz6fvz" /> Prekliči ponudbo
            </Button>}
          {mine && !isAdmin && <Button variant="secondary" onClick={() => openException(viewer.id)} data-id="element-1051" data-mp="v0qnzw479mrp">
              <ShieldAlertIcon className="h-4 w-4" aria-hidden="true" data-id="element-1052" data-mp="q377sqnxyzx4" /> Predlagaj izjemo
            </Button>}
        </div>}

      {!trust && <p className="mt-4 text-xs text-muted" data-id="element-1053" data-mp="h3bw8kqh52wk">Časovnik in dnevnik časa ne nadomestita potrditve skrbnika. Brez obveznih kontrolnih podtočk.</p>}
      <p className="mt-2 text-xs text-muted" data-id="element-1054" data-mp="c4hbvecuz6ac">
        {isAdmin ? 'Vrednosti, nosilce in pogostost urejaš v Nastavitve družine › Naloge.' : 'To obveznost je nastavil skrbnik. Lahko jo obkljukaš, oddaš v pregled, predlagaš izjemo ali (če je prenosljiva) ponudiš; urejaš samo svoje vsebine.'}
      </p>
    </Sheet>;
}